import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { InputAdornment, Link, Stack, Typography } from '@mui/material';
import { Form, Field } from 'react-final-form';
import DatePickerFieldAdapter from 'components/shared/field-adapters/DatePickerFieldAdapter';
import { Email, Person } from '@mui/icons-material';
import PrimaryLoadingButton from 'components/shared/buttons/PrimaryLoadingButton';
import PasswordFieldAdapter from 'components/shared/field-adapters/PasswordFieldAdapter';
import PasswordStrengthHints from 'components/shared/PasswordStrengthHints';
import SelectFieldAdapter from 'components/shared/field-adapters/SelectFieldAdapter';
import TextFieldAdapter from 'components/shared/field-adapters/TextFieldAdapter';
import GoogleAuthButton from 'components/auth/portal/GoogleAuthButton';
import { getGenderOptions } from 'common/constants/options';
import requestStatus from 'common/constants/status';
import routes from 'common/routes';
import validateRegisterForm from 'common/validations/form/register';
import authSlice from 'store/modules/auth';

const registerInitialValues = {
  first_name: '',
  last_name: '',
  email: '',
  password: '',
  password_confirmation: '',
  birthdate: null,
  gender: '',
  ui_language: 'es'
};

/**
 * Formulario de REGISTRO (extraído de la vieja pantalla Register): los 7 campos,
 * checklist de contraseña, validación y redirección a register-success intactos.
 * El card/hero/logo los pone AuthCard.
 */
function RegisterForm() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation(['buttons', 'labels', 'common', 'options']);
  const hasNavigated = useRef(false);
  const [hasRegistered, setHasRegistered] = useState(false);

  const status = useSelector((state) => state.auth.status);

  const genderOptions = getGenderOptions(t);

  useEffect(() => {
    dispatch(authSlice.actions.statusReset());
  }, [dispatch]);

  useEffect(() => {
    if (hasRegistered && status === requestStatus.success && !hasNavigated.current) {
      hasNavigated.current = true;
      navigate(routes.registerSuccess.path, { replace: true });
    }
  }, [hasRegistered, navigate, status]);

  const onRegister = (values) => {
    setHasRegistered(true);
    dispatch(authSlice.actions.register(values));
  };

  return (
    <Form
      initialValues={registerInitialValues}
      validate={validateRegisterForm}
      onSubmit={onRegister}
      render={({ handleSubmit }) => (
        <form onSubmit={handleSubmit}>
          <Stack spacing={2}>
            <Stack spacing={3}>
              <Stack spacing={2} sx={{ width: '100%' }}>
                <Stack direction="row" spacing={1}>
                  <Field
                    id="first_name"
                    name="first_name"
                    component={TextFieldAdapter}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="start">
                          <Person />
                        </InputAdornment>
                      )
                    }}
                    label={t('firstname', { count: 1, ns: 'labels' })}
                    type="text"
                    fullWidth
                    required
                  />
                  <Field
                    id="last_name"
                    name="last_name"
                    component={TextFieldAdapter}
                    label={t('lastname', { count: 1, ns: 'labels' })}
                    type="text"
                    fullWidth
                    required
                  />
                </Stack>
                <Field
                  id="email"
                  name="email"
                  component={TextFieldAdapter}
                  autoComplete="email"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="start">
                        <Email />
                      </InputAdornment>
                    )
                  }}
                  label={t('email', { ns: 'labels' })}
                  type="text"
                  fullWidth
                  required
                />
                <Field
                  id="birthdate"
                  name="birthdate"
                  component={DatePickerFieldAdapter}
                  label={t('birthdate', { ns: 'labels' })}
                  renderInputProps={{ fullWidth: true, required: true }}
                  disableFuture
                />
                <Field
                  id="gender"
                  name="gender"
                  component={SelectFieldAdapter}
                  label={t('gender', { ns: 'labels' })}
                  options={genderOptions}
                  fullWidth
                  required
                />
                <Field
                  id="password"
                  name="password"
                  component={PasswordFieldAdapter}
                  label={t('password', { ns: 'labels' })}
                  autoComplete="new-password"
                  fullWidth
                  required
                />
                {/* Checklist en vivo de criterios: visible solo con foco o texto. */}
                <Field name="password" subscription={{ value: true, active: true }}>
                  {({ input: { value: passwordValue }, meta: { active: passwordActive } }) => (
                    <PasswordStrengthHints
                      value={passwordValue}
                      visible={Boolean(passwordActive || passwordValue)}
                    />
                  )}
                </Field>
                <Field
                  id="password_confirmation"
                  name="password_confirmation"
                  component={PasswordFieldAdapter}
                  label={t('confirmPassword', { ns: 'labels' })}
                  autoComplete="new-password"
                  fullWidth
                  required
                />
              </Stack>
              <PrimaryLoadingButton
                disabled={status === requestStatus.loading}
                loading={status === requestStatus.loading}
                label={t('auth.register', { ns: 'common', defaultValue: 'Registrarse' })}
                type="submit"
              />
              <GoogleAuthButton />
            </Stack>
            <Typography variant="subtitle2" align="center" sx={{ color: 'text.secondary' }}>
              {t('auth.hasAccount', { ns: 'common', defaultValue: '¿Ya tienes una cuenta?' })}{' '}
              <Link component={RouterLink} to={routes.login.path} underline="always" sx={{ color: 'secondary.main' }}>
                {t('auth.loginHere', { ns: 'common', defaultValue: 'Ingresa aquí' })}
              </Link>
            </Typography>
          </Stack>
        </form>
      )}
    />
  );
}

export default RegisterForm;
